import React from 'react';
import DatePicker from 'react-datepicker';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import "react-datepicker/dist/react-datepicker.css";
import { registerLocale } from "react-datepicker";
import bg from 'date-fns/locale/bg';

// Register Bulgarian locale
registerLocale('bg', bg);

const CustomDatePicker = ({
    name,
    value,
    onChange,
    errors,
    label,
    required = false,
    isClearable = true,
    className = '',
    placeholderText,
    minDate,
    maxDate,
    disabled = false,
    showTimeSelect = false,
    dateFormat = "dd.MM.yyyy",
    timeFormat = "HH:mm",
    timeIntervals = 15,
    timeCaption = "Time",
    isSmall = false,
    useDefaultSize = false,
    monthsShown = 1,
    renderInPortal = false,
    popperPlacement = "bottom-start",
}) => {
    const { t } = useTranslation();
    
    const handleDateChange = (date) => {
        if (date === null) {
            onChange(null);
        } else {
            const newDate = new Date(date);
            if (!isNaN(newDate.getTime())) {
                // Set time to noon (12:00:00) to avoid timezone issues
                newDate.setHours(12, 0, 0, 0);
                onChange(newDate);
            }
        }
    };

    const getInputClass = () => {
        if (isSmall) return 'small-input';
        if (useDefaultSize) return 'default-input';
        return 'form-control';
    };

    // Safe date parsing with support for multiple formats
    // Sets time to noon (12:00:00) to avoid timezone issues
    const parseDate = (dateValue) => {
        if (!dateValue || dateValue === '') return null;
        
        // If already a Date object
        if (dateValue instanceof Date) {
            if (isNaN(dateValue.getTime())) return null;
            const date = new Date(dateValue);
            date.setHours(12, 0, 0, 0);
            return date;
        }
        
        // If it's a string, try to parse dd.MM.yyyy format first
        if (typeof dateValue === 'string') {
            const parts = dateValue.split('.');
            if (parts.length === 3) {
                const [day, month, year] = parts;
                const date = new Date(year, month - 1, day, 12, 0, 0, 0);
                if (!isNaN(date.getTime())) {
                    return date;
                }
            }
        }
        
        // Fallback: try standard Date parsing, set to noon
        const date = new Date(dateValue);
        if (isNaN(date.getTime())) return null;
        date.setHours(12, 0, 0, 0);
        return date;
    };
    
    let selectedDate = null;
    if (value !== undefined && value !== null && value !== '') {
        selectedDate = parseDate(value);
        if (selectedDate) {
        } else {
            console.error(`❌ [CustomDatePicker] ${name} Invalid date:`, value);
        }
    } else {
    }

    const PortalPopperContainer = ({ children }) => {
        if (typeof document === 'undefined') return children;
        return createPortal(children, document.body);
    };

    const datePickerProps = {
        selected: selectedDate,
        onChange: handleDateChange,
        dateFormat: dateFormat,
        locale: "bg",
        className: `${getInputClass()} ${errors?.[name] ? 'is-invalid' : ''} ${className}`,
        placeholderText: placeholderText || t('common.dateFormat'),
        isClearable: isClearable,
        minDate: minDate,
        maxDate: maxDate,
        disabled: disabled,
        showTimeSelect: showTimeSelect,
        timeFormat: timeFormat,
        timeIntervals: timeIntervals,
        timeCaption: timeCaption,
        showMonthDropdown: true,
        showYearDropdown: true,
        dropdownMode: "select",
        scrollableYearDropdown: true,
        yearDropdownItemNumber: 50,
        monthsShown: monthsShown,
        fixedHeight: true,
        forceShowMonthNavigation: true,
        preventOpenOnFocus: true,
        popperPlacement: popperPlacement,
        popperClassName: renderInPortal ? 'datepicker-popper-overlay' : '',
        popperProps: renderInPortal ? { strategy: 'fixed' } : undefined,
        popperContainer: renderInPortal ? PortalPopperContainer : undefined,
    };

    return (
        <>
            <DatePicker {...datePickerProps} />
            {errors?.[name] && !value && (
                <div className="invalid-feedback d-block">{errors[name].message}</div>
            )}
        </>
    );
};

export default CustomDatePicker; 